import axios from 'axios'
import {
  isPortalAccessRevokedError,
  portalAccessRevokedMessage
} from '@/services/vendorPortalSocket'

const TOKEN_KEY = 'vendorToken'
const USER_KEY = 'vendorUser'

function authHeaders() {
  const token = localStorage.getItem(TOKEN_KEY)
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function api(path) {
  const base = process.env.VUE_APP_API_URL || ''
  return `${base}/vendor-portal${path}`
}

function isVendorPortalRequest(config) {
  const url = String(config?.url || '')
  return url.includes('/vendor-portal')
}

if (!axios.__vendorPortalRevokeInterceptor) {
  axios.__vendorPortalRevokeInterceptor = true
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        if (isVendorPortalRequest(error?.config) && isPortalAccessRevokedError(error)) {
          window.dispatchEvent(
            new CustomEvent('vendor-portal-access-revoked', {
              detail: {
                code: 'PORTAL_ACCESS_REVOKED',
                message: portalAccessRevokedMessage(error)
              }
            })
          )
        }
      } catch (_) {
        /* ignore */
      }
      return Promise.reject(error)
    }
  )
}

export default {
  getSession() {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || 'null')
    } catch (_) {
      return null
    }
  },
  isLoggedIn() {
    return Boolean(localStorage.getItem(TOKEN_KEY) && this.getSession())
  },
  saveSession(token, vendor) {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(vendor))
  },
  clearSession() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },
  accountStatus(email, inviteToken) {
    return axios.post(api('/account-status'), {
      email,
      ...(inviteToken ? { inviteToken } : {})
    })
  },
  sendOtp(email, inviteToken) {
    return axios.post(api('/send-otp'), {
      email,
      ...(inviteToken ? { inviteToken } : {})
    })
  },
  passwordLogin(payload) {
    return axios.post(api('/password-login'), payload)
  },
  verifyOtp(payload) {
    return axios.post(api('/verify-otp'), payload)
  },
  completeLogin(payload) {
    return axios.post(api('/complete-login'), payload)
  },
  previewInvite(token) {
    return axios.get(api(`/invite/${encodeURIComponent(token)}`))
  },
  listBills() {
    return axios.get(api('/bills'), { headers: authHeaders() })
  },
  getBill(id) {
    return axios.get(api(`/bills/${id}`), { headers: authHeaders() })
  },
  previewBillAttachment(billId, attachmentId) {
    return axios.get(api(`/bills/${billId}/attachments/${attachmentId}`), {
      headers: authHeaders(),
      responseType: 'blob'
    })
  },
  listPayments() {
    return axios.get(api('/payments'), { headers: authHeaders() })
  },
  getPayment(id) {
    return axios.get(api(`/payments/${id}`), { headers: authHeaders() })
  },
  getMe() {
    return axios.get(api('/me'), { headers: authHeaders() })
  },
  updateProfile(payload) {
    return axios.put(api('/me'), payload, { headers: authHeaders() })
  },
  updateSessionVendor(vendor) {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token || !vendor) return
    this.saveSession(token, vendor)
  },
  getLogo() {
    return axios.get(api('/me/logo'), {
      headers: authHeaders(),
      responseType: 'blob'
    })
  },
  getCompanyLogo() {
    return axios.get(api('/company/logo'), {
      headers: authHeaders(),
      responseType: 'blob'
    })
  },
  uploadLogo(file) {
    const formData = new FormData()
    formData.append('logo', file)
    return axios.post(api('/me/logo'), formData, {
      headers: {
        ...authHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  removeLogo() {
    return axios.delete(api('/me/logo'), { headers: authHeaders() })
  },
  listConsultants() {
    return axios.get(api('/consultants'), { headers: authHeaders() })
  },
  listConsultantTimesheets(consultantId) {
    return axios.get(api(`/consultants/${encodeURIComponent(consultantId)}/timesheets`), {
      headers: authHeaders()
    })
  },
  listPaymentTerms() {
    return axios.get(api('/payment-terms'), { headers: authHeaders() })
  },
  nextInvoiceNumber(date) {
    const params = {}
    if (date) params.date = date
    return axios.get(api('/next-invoice-number'), {
      headers: authHeaders(),
      params
    })
  },
  listBillNotes(billId) {
    return axios.get(api(`/bills/${billId}/notes`), { headers: authHeaders() })
  },
  createBillNote(billId, body) {
    return axios.post(api(`/bills/${billId}/notes`), { body }, { headers: authHeaders() })
  },
  uploadBill(formData) {
    return axios.post(api('/bills'), formData, {
      headers: {
        ...authHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}
